import { useCallback, useMemo, useState, type ReactNode } from "react";
import { listProjects, readProject, type ProjectOverview, type ProjectSummary } from "../api/projectsApi";
import { useSession } from "../session/sessionContext";
import { isAllowed } from "../api/access/decisions";
import { sessionDecision } from "../session/sessionMachine";
import { useLoad } from "../shared/loading/useLoad";
import { readStoredString, writeStoredString } from "../shared/storage/storedString";
import { ProjectContext, SELECTED_PROJECT_STORAGE_KEY, type ProjectContextValue } from "./projectContext";

const noProject = (): Promise<null> => Promise.resolve(null);

/** Loads the visible projects and the chosen one; remembers the choice in the browser. Only
 * administrators can choose the DMW core (F62): for anyone else it is never chosen, remembered
 * or opened from its settings' address. */
export default function ProjectProvider({ children }: { children: ReactNode }) {
  const { state: projects, reload: reloadProjects } = useLoad(listProjects);
  const [storedProjectId, setStoredProjectId] = useState(() => readStoredString(SELECTED_PROJECT_STORAGE_KEY));
  const administrator = isAllowed(sessionDecision(useSession().session, "manage_installation"));
  const mayChoose = useCallback((project: ProjectSummary) => !project.installationOwned || administrator, [administrator]);

  // A remembered project the user cannot see or choose any more counts as no choice.
  const visibleProjects = projects.status === "loaded" ? projects.value.filter(mayChoose) : null;
  const selectedProjectId =
    visibleProjects === null
      ? null
      : (visibleProjects.find((project) => project.projectId === storedProjectId)?.projectId ??
        (visibleProjects.length === 1 ? (visibleProjects[0]?.projectId ?? null) : null));

  const loadSelectedProject = useCallback(
    (signal: AbortSignal): Promise<ProjectOverview | null> =>
      selectedProjectId === null ? noProject() : readProject(selectedProjectId, signal),
    [selectedProjectId],
  );
  const { state: selectedProjectState, refresh: refreshSelectedProject } = useLoad(loadSelectedProject);

  const selectProject = useCallback((projectId: string) => {
    writeStoredString(SELECTED_PROJECT_STORAGE_KEY, projectId);
    setStoredProjectId(projectId);
  }, []);

  const value = useMemo<ProjectContextValue>(() => {
    let selectedProject: ProjectContextValue["selectedProject"] = null;
    if (selectedProjectId !== null) {
      if (selectedProjectState.status === "loaded") {
        selectedProject =
          selectedProjectState.value === null ? null : { status: "loaded", value: selectedProjectState.value };
      } else {
        selectedProject = selectedProjectState;
      }
    }
    return { projects, selectedProjectId, selectedProject, selectProject, mayChoose, reloadProjects, refreshSelectedProject };
  }, [projects, selectedProjectId, selectedProjectState, selectProject, mayChoose, reloadProjects, refreshSelectedProject]);

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
}
